import { getRequest, putRequest, getRequestV5, postRequest, deleteRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import { Fingerprint, FingerprintCoordinate } from 'lib/types/fingerprints'

// Safety cap so a bad `meta` (or an endpoint that never reports the last page)
// can never spin the pagination loop forever.
const MAX_FINGERPRINT_PAGES = 100

export interface FingerprintsPageResult {
  data: Fingerprint[]
  currentPage: number
  totalPages: number
  totalCount: number
}

// Fetch a single page of fingerprints for a floor. Follows the v5 query-param
// contract used by the other v5 list endpoints: `page` for pagination and a
// per-field `filter[<field>]=<value>` (here scoped to the selected floor).
export const getFingerprintsPage = ({
  orgId,
  floorId,
  page = 1,
  limit,
}: {
  orgId: string
  floorId: string
  page?: number
  limit?: number
}): Promise<FingerprintsPageResult> => {
  const qs: string[] = [`page=${page}`]
  if (limit) qs.push(`limit=${limit}`)
  if (floorId) qs.push(`filter[floorId]=${encodeURIComponent(floorId)}`)

  return getRequest({
    endpoint: `api/v5/fingerprints?${qs.join('&')}`,
    header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
  }).then((response: any) => {
    const data: Fingerprint[] = response?.data?.data ?? []
    const meta = response?.data?.meta ?? {}
    // v5 endpoints are inconsistent about meta casing; accept either shape.
    const currentPage: number = meta.currentPage ?? meta['current-page'] ?? page
    const totalCount: number = meta.totalCount ?? meta['total-count'] ?? data.length
    const totalPages: number = meta.totalPages ?? meta['total-pages'] ?? 1
    return { data, currentPage, totalCount, totalPages }
  })
}

// Fetch every fingerprint for a floor by walking the paginated endpoint.
export const getAllFingerprintsForFloor = async ({
  orgId,
  floorId,
}: {
  orgId: string
  floorId: string
}): Promise<Fingerprint[]> => {
  const all: Fingerprint[] = []
  let page = 1

  while (page <= MAX_FINGERPRINT_PAGES) {
    const { data, totalPages } = await getFingerprintsPage({ orgId, floorId, page })
    all.push(...data)
    if (data.length === 0 || page >= totalPages) break
    page += 1
  }

  return all
}

export const getFingerprints = (orgId: string): Promise<any[]> => {
  return getRequest({
    endpoint: `api/v5/fingerprints`,
    header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
  })
    .then((response: any) => {
      return response.data.data // ✅ return just the array
    })
    .catch((error: Error) => {
      console.error('Error fetching fingerprints:', error)
      throw error
    })
}

export const postFingerprints = (
  orgId: string,
  rssiMap: Record<string, number>,
  floorId: string,
  coords: { lat: number; lon: number },
): Promise<void> => {
  console.log('Posting fingerprint payload:', {
    data: rssiMap,
    floorId,
    lat: coords.lat,
    lon: coords.lon,
    orgId,
  })
  return postRequest({
    endpoint: `api/v5/fingerprints`,
    header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
    data: { data: rssiMap, floorId: floorId, lat: coords.lat, lon: coords.lon, orgId: orgId },
  })
    .then((response: any) => {
      return response.data
    })
    .catch((error: Error) => {
      console.error('Error posting fingerprint', error)
      throw error
    })
}

export const deleteFingerprint = (fingerprintId: string): Promise<void> => {
  console.log('Deleting fingerprint:', { fingerprintId })

  return deleteRequest({
    endpoint: `api/v5/fingerprints/${fingerprintId}`,
    header: {},
  })
    .then(() => {
      console.log('Fingerprint deleted successfully:', fingerprintId)
    })
    .catch((error: Error) => {
      console.error('Error deleting fingerprint:', error)
      throw error
    })
}

// Bulk-delete every fingerprint captured at the given coordinate(s) on a floor.
// Each (lat, lon) spot holds a cluster of samples, so this removes the whole
// cluster in one request via DELETE /api/v5/fingerprints/by_coordinates.
export const deleteFingerprintsByCoordinates = ({
  orgId,
  floorId,
  coordinates,
}: {
  orgId: string
  floorId: string
  coordinates: FingerprintCoordinate[]
}): Promise<void> => {
  return deleteRequest({
    endpoint: `api/v5/fingerprints/by_coordinates`,
    header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
    data: { floorId, coordinates },
  })
    .then(() => {
      console.log('Deleted fingerprint cluster(s):', { floorId, coordinates })
    })
    .catch((error: Error) => {
      console.error('Error deleting fingerprint cluster:', error)
      throw error
    })
}
